import { Download, Play, Film, Scissors, X } from 'lucide-react'

// Panneau des clips generes. Collapsable sur desktop (barre verticale 42px
// fermee <-> 280px ouverte), force ouvert en pleine largeur sur mobile.
// Un moment apparait ici des qu'il a un mp4_url. Clic sur une ligne -> lecture
// dans le player principal.

const BLUE  = '#4F8EF7'
const INK   = '#1a1916'
const MUTED = 'rgba(26,25,22,0.5)'
const RULE  = 'rgba(26,25,22,0.1)'

const DISP = "'Barlow Condensed', sans-serif"
const BODY = "'Space Grotesk', sans-serif"
const MONO = "'JetBrains Mono', 'Space Grotesk', monospace"

const TYPE_META = {
  goal:               { label: 'But',        color: '#4F8EF7' },
  chance:             { label: 'Occasion',   color: '#f59e0b' },
  loss:               { label: 'Perte',      color: '#dc2626' },
  recovery:           { label: 'Récup',      color: '#3DBF7A' },
  transition_for:     { label: 'Trans off.', color: '#3b82f6' },
  transition_against: { label: 'Trans sub.', color: '#991b1b' },
  defensive_phase:    { label: 'Défense',    color: '#6b7280' },
  set_piece:          { label: 'CPA',        color: '#a855f7' },
  tactical_note:      { label: 'Clip',       color: '#9ca3af' },
  player_action:      { label: 'Geste',      color: '#6366f1' },
}

function fmtClock(s) {
  if (!Number.isFinite(s) || s < 0) s = 0
  const total = Math.floor(s)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const sec = total % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(sec).padStart(2, '0')
  return h > 0 ? `${String(h).padStart(2, '0')}:${mm}:${ss}` : `${mm}:${ss}`
}

function clipName(m) {
  const meta = TYPE_META[m.moment_type] || { label: 'Clip' }
  return `${meta.label} ${fmtClock(m.timecode_start)}`
}

function clipDuration(m) {
  if (Number.isFinite(m.timecode_end) && Number.isFinite(m.timecode_start) && m.timecode_end > m.timecode_start) {
    return m.timecode_end - m.timecode_start
  }
  return null
}

export default function ClipsPanel({
  moments = [],
  onSelectClip,
  onDownloadClip,
  isOpen = false,
  onToggle,
  isMobile = false,
  locked = false,
}) {
  const clips = moments
    .filter(m => !!m.mp4_url)
    .sort((a, b) => a.timecode_start - b.timecode_start)

  // Verrou pendant le mode annotation du studio. Selectionner un clip fait
  // seekTo + play() : c'est le seul point d'entree du studio qui relance la
  // lecture, donc celui qui casserait le plus surement la "frame figee".
  // Le panneau est neutralise en entier, telechargement compris : laisser un
  // bouton actif dans un panneau grise casserait le modele mental.
  // `inert` (chaine vide, React 18) coupe le focus et le declenchement clavier
  // Entree/Espace sur une tuile ; pointer-events couvre la souris.
  const lockAttr  = locked ? { inert: '' } : {}
  // 0.7 : voir NavLock dans VideoStudio.jsx, meme raisonnement sur l'alpha
  // effectif. Une zone verrouillee reste lisible ; seuls les boutons
  // desactives du header descendent a 0.4, ou la semantique est differente.
  const lockStyle = locked ? { pointerEvents: 'none', opacity: 0.7 } : null

  // ─── Etat ferme (desktop uniquement) : barre verticale cliquable ──────────
  if (!isMobile && !isOpen) {
    return (
      <button
        onClick={onToggle}
        aria-label={`Ouvrir le panneau clips (${clips.length})`}
        aria-expanded={false}
        {...lockAttr}
        style={{
          width: 42, flexShrink: 0, alignSelf: 'stretch',
          background: 'rgba(255,255,255,0.04)',
          border: `1px solid ${RULE}`, borderRadius: 8,
          cursor: 'pointer', padding: '14px 0',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
          transition: 'opacity .15s',
          ...lockStyle,
        }}
      >
        <Scissors size={16} color={BLUE} />
        <span style={{ fontFamily: DISP, fontWeight: 700, fontSize: 18, color: INK, lineHeight: 1 }}>
          {clips.length}
        </span>
        <span style={{
          fontFamily: MONO, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase',
          color: MUTED, writingMode: 'vertical-rl',
        }}>
          Clip{clips.length > 1 ? 's' : ''}
        </span>
      </button>
    )
  }

  // ─── Etat ouvert (ou mobile) ──────────────────────────────────────────────
  return (
    <div
      {...lockAttr}
      style={{
        background: '#fff', border: `1px solid ${RULE}`, borderRadius: 8,
        display: 'flex', flexDirection: 'column',
        width: isMobile ? '100%' : 280, flexShrink: 0, alignSelf: 'stretch',
        transition: 'opacity .15s',
        ...lockStyle,
      }}
    >
      {/* En-tete */}
      <div style={{
        padding: '12px 14px', borderBottom: `1px solid ${RULE}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 14, height: 1, background: BLUE }} />
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: BLUE }}>
            Clips ({clips.length})
          </span>
        </div>
        {!isMobile && (
          <button
            onClick={onToggle}
            aria-label="Fermer le panneau clips"
            aria-expanded={true}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: MUTED, padding: 4, borderRadius: 6 }}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {clips.length === 0 ? (
        <div style={{ padding: '28px 18px', textAlign: 'center' }}>
          <Film size={22} color={MUTED} style={{ marginBottom: 8 }} />
          <p style={{ fontFamily: MONO, fontSize: 10, color: MUTED, margin: 0, lineHeight: 1.6, letterSpacing: '.02em' }}>
            Aucun clip généré · Utilise « Découper un clip » pour commencer
          </p>
        </div>
      ) : (
        <div style={{ maxHeight: isMobile ? 'none' : 520, overflowY: 'auto' }}>
          {clips.map((m, idx) => {
            const meta = TYPE_META[m.moment_type] || { label: 'Clip', color: MUTED }
            const dur = clipDuration(m)
            return (
              <div
                key={m.id}
                role="button"
                tabIndex={0}
                aria-label={`Lire le clip ${clipName(m)}`}
                onClick={() => onSelectClip && onSelectClip(m)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectClip && onSelectClip(m) } }}
                style={{
                  padding: '10px 12px',
                  borderBottom: idx === clips.length - 1 ? 'none' : `1px solid ${RULE}`,
                  display: 'flex', alignItems: 'center', gap: 10,
                  cursor: 'pointer', transition: 'background .12s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(79,142,247,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{
                  width: 46, height: 26, borderRadius: 4, flexShrink: 0, overflow: 'hidden',
                  background: m.thumbnail_url ? '#000' : 'linear-gradient(135deg,#2a2a2a,#3a3a3a)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {m.thumbnail_url
                    ? <img src={m.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <Play size={12} color="rgba(255,255,255,0.7)" />
                  }
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: DISP, fontWeight: 600, fontSize: 15, color: INK, lineHeight: 1.1,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {clipName(m)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                    <span style={{
                      fontFamily: MONO, fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase',
                      padding: '2px 6px', borderRadius: 3,
                      background: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}40`,
                    }}>
                      {meta.label}
                    </span>
                    {dur != null && (
                      <span style={{ fontFamily: MONO, fontSize: 9, color: MUTED }}>{dur.toFixed(1)}s</span>
                    )}
                  </div>
                </div>

                <button
                  onClick={(e) => { e.stopPropagation(); onDownloadClip && onDownloadClip(m) }}
                  aria-label={`Télécharger le clip ${clipName(m)}`}
                  style={{
                    width: 32, height: 32, borderRadius: 6, flexShrink: 0,
                    background: 'transparent', border: `1px solid ${RULE}`, color: MUTED,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all .15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = BLUE; e.currentTarget.style.color = BLUE }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = RULE; e.currentTarget.style.color = MUTED }}
                >
                  <Download size={14} />
                </button>
              </div>
            )
          })}
        </div>
      )}

    </div>
  )
}
